import {
  BrowsingCriteria,
  CartBrowsingCriteria,
  MarketingTag,
  Product,
  ProductLineAttr,
  RateSegment,
} from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { PaginationDTO, PaginationInput, TitleObject } from './general';

export interface Catalog {
  id: string;
  rateSegment: RateSegment;
  productLineAttributes: ProductLineAttr[];
  marketingTags: MarketingTag[];
}

export interface PortfolioRequest {
  sponsorId: string;
  agencyId: string;
  rateSegment: RateSegment;
  productLineId: ProductLineId;
  bc: CartBrowsingCriteria;
  groupId: string;
}

export interface ProductTemplate {
  id: string;
  name: string;
  internalName: string;
  rateSegment: RateSegment;
  effectiveFrom: string;
  effectiveTo: string;
  createdOn: string;
  createdBy: PerformedBy;
  creationDateTime: string;
  productLineId: ProductLineId;
  filterBC: ProductTemplateBC;
  planSummary: string;
  formAnswers: FormAnswers;
  checklist: ProductTemplateChecklist;
  status: ProductTemplateStatus;
  maxWaiverMessage?: MaxWaiver;
}

export interface InputGenerateProductTemplate {
  id: string;
  name: string;
  productLineId: ProductLineId;
  planSummary: string;
  formAnswers: FormAnswers;
  maxWaiverMessage?: MaxWaiver;
}

export interface MaxWaiver {
  message: string;
  benefits: string[];
}

export interface PerformedBy {
  id: string;
  name: string;
}

export interface ProductTemplateInput {
  sponsorId?: string;
  page: number;
  filter: ProductTemplateInputFilter;
  sortBy?: ProductTemplateSortBy;
  sortOrder?: ProductTemplateSortOrder;
}

export interface ProductTemplateInputFilter {
  status: ProductTemplateStatus | null;
  productName?: string | null;
  productLine?: ProductLineId | null;
  state?: string | null;
  effectiveYear?: string | null;
}

export enum ProductTemplateSortBy {
  NAME = 'NAME',
  DATE = 'DATE',
  ID = 'ID',
}

export enum ProductTemplateSortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum ProductTemplateStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  READY = 'READY',
  ARCHIVE = 'ARCHIVE',
  INITIAL_SEED = 'INITIAL_SEED',
}

export interface ShelfProductInfo {
  product: Product;
  productTemplate: ProductTemplate | null;
}

export interface ShelfProductFilter {
  productName: string | null;
  productLine: string | null;
  status: ProductTemplateStatus | null;
  effectiveYear: string | null;
}

export interface ShelfProductTemplateInput {
  sponsorId?: string;
  page: number;
  filter: ShelfProductFilter;
  sortBy?: ProductTemplateSortBy;
  sortOrder?: ProductTemplateSortOrder;
}

/**
 * General product template statuses with internal IM_GENERATED status(FE only)
 */
export type ProductTemplateExtendedStatus = ProductTemplateStatus | 'IM_GENERATED';

export const PRODUCT_TEMPLATE_STATUS_TITLES: TitleObject<ProductTemplateExtendedStatus> = Object.freeze({
  IM_GENERATED: 'IM Generated',
  IN_PROGRESS: 'In Progress',
  READY: 'Ready',
  INITIAL_SEED: 'Initial Seed',
  ARCHIVE: 'Archive',
});

export interface CreateProductTemplateInput {
  productLine: ProductLineId;
  productName: string;
  internalName: string;
  effectiveFrom: string;
  effectiveTo: string;
  rateSegment: RateSegment;
  bc?: ProductTemplateBC | null;
}

export interface UpdateProductTemplateInput {
  id: string;
  name: string;
  internalName: string;
  effectiveFrom: string;
  effectiveTo: string;
  productLineId: ProductLineId;
  state: string;
}

export interface ProductTemplateBC {
  state?: string[];
}

export interface FormAnswers {
  policyAttributes: Record<string, string>;
  planAttributes: FormAttributes;
  customAttributes: FormAttributes;
  engValues: Record<string, EngValue[]>;
}

export interface InputFormAnswers {
  policyAttributes: Record<string, string> | null;
  planAttributes: FormAttributes;
  customAttributes: FormAttributes;
  engValues: Record<string, EngValue[]>;
}

export interface EngValue {
  label: string;
  limitationKey: string;
  productOffering: string;
  labelKey: string;
}

export interface FormAttributes {
  [key: string]: Record<string, string>;
}

export interface ProductTemplateChecklist {
  checklistOptions: ProductTemplateChecklistOption[];
}
export interface ProductTemplateChecklistOption {
  card: ProductTemplateChecklistCard;
  buttonState: ButtonState;
  completionStatus: CompletionStatus;
}

export enum ProductTemplateChecklistCard {
  POLICY_ATTRIBUTES = 'POLICY_ATTRIBUTES',
  PLAN_ATTRIBUTES = 'PLAN_ATTRIBUTES',
  GENERATE_PDF = 'GENERATE_PDF',
  SAVE_PRODUCT_TEMPLATE = 'SAVE_PRODUCT_TEMPLATE',
  BENEFIT_SUMMARY = 'BENEFIT_SUMMARY',
}

export enum ButtonState {
  VIEW_EDIT = 'VIEW_EDIT',
  UNAVAILABLE = 'UNAVAILABLE',
  CONTINUE = 'CONTINUE',
}

export enum CompletionStatus {
  COMPLETED = 'COMPLETED',
  READY_TO_BEGIN = 'READY_TO_BEGIN',
  CANNOT_COMPLETE_YET = 'CANNOT_COMPLETE_YET',
  IN_PROGRESS = 'IN_PROGRESS',
}

export interface CartItemPortfolioCreationPresenter {
  portfolioId: string;
  browsingCriteria: BrowsingCriteria;
}

export interface ProposalProductTagData {
  portfolioId: string;
  productId: string;
  periodId: string;
}

export interface ProposalProductTagDataWithTag extends ProposalProductTagData {
  tag: string;
}

export type PlanAttributeType = 'FILLED_COVERED' | 'FILLED_UNCOVERED' | 'UNFILLED';

export interface RFQCarrierAvailability {
  productLineId: ProductLineId;
  carriers: Carrier[];
}

export interface Carrier {
  id: string;
  name: string;
  logo: string;
  sponsorId: string;
  defaultRecipientEmail: string;
}

export interface InitialSeedTemplateFilter {
  productName: string | null;
  hasValidationErrors: boolean | null;
  doesNotHaveBenefitsFile: boolean | null;
  [key: string]: unknown;
}

export interface InitialSeedProductTemplate {
  groupId: string;
  clientGroupId: string;
  template: ProductTemplate;
  clientGroupName: string;
  jsonLink: string;
  benefitSummaryLink?: string;
  errorMessage?: string;
}

export type InitialSeedProductTemplatePage = PaginationDTO<InitialSeedProductTemplate>;

export interface InitialSeedInput
  extends PaginationInput<ProductTemplateSortBy, ProductTemplateSortOrder, InitialSeedTemplateFilter> {
  sponsorId: string;
}

export enum PlanAttributeCategory {
  CUSTOM = 'CUSTOM',
  REQUIRED = 'REQUIRED',
}

export interface RateInput {
  id: string;
  key: string;
  displayKey: string;
  value: number;
}

export interface RatesOverride {
  portfolioId: string;
  productId: string;
  bcIndex: number;
  rates: RateInput[];
}
